import * as React from 'react';
import { UseFormReturn } from 'react-hook-form';

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { InputPasswordCds } from '@/components/ui/input-password-cds';

/**
 * Props for the FormFieldPasswordCds component.
 *
 * @typedef {Object} FormFieldPasswordCdsProps
 * @property {Omit<React.ComponentProps<'input'>, 'form'>} - Inherits all input properties except 'form'.
 * @property {UseFormReturn} form - The form instance returned by the useForm hook.
 * @property {string | React.ReactNode} [label] - Optional label for the password field.
 * @property {string | React.ReactNode} [description] - Optional description for the password field.
 * @property {string | React.ReactNode} [errorMessage] - Optional error message to display.
 * @property {string | React.ReactNode} [linkLabel] - Optional label for a link associated with the password field.
 * @property {string} [linkUrl] - Optional URL for the link associated with the password field.
 */
export type FormFieldPasswordCdsProps = Omit<React.ComponentProps<'input'>, 'form'> & {
  form: UseFormReturn;
  label?: string | React.ReactNode;
  description?: string | React.ReactNode;
  errorMessage?: string | React.ReactNode;
  linkLabel?: string | React.ReactNode;
  linkUrl?: string;
};

/**
 * `FormFieldPasswordCds` is a React forwardRef component that renders a password input field
 * within a form. It integrates with a form control system and provides additional features
 * such as labels, descriptions, error messages, and a link for password recovery.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {string} [className] - Optional additional class names for the component.
 * @param {string} [placeholder] - Optional placeholder text for the password input field.
 * @param {string | React.ReactNode} [label] - Optional label text for the password input field.
 * @param {UseFormReturn} form - The form control object, required for form integration.
 * @param {string} name - The name of the form field, required for form integration.
 * @param {string | React.ReactNode} [description] - Optional description text for the form field.
 * @param {string | React.ReactNode} [errorMessage] - Optional error message text for the form field.
 * @param {string | React.ReactNode} [linkLabel='Forgot your password?'] - Optional label for the password recovery link.
 * @param {string} [linkUrl] - Optional URL for the password recovery link.
 * @param {React.Ref<HTMLInputElement>} ref - The ref to be forwarded to the input element.
 *
 * @throws {Error} Throws an error if the `form` or `name` props are not provided.
 *
 * @returns {JSX.Element} The rendered password input form field component.
 */
const FormFieldPasswordCds = React.forwardRef<HTMLInputElement, FormFieldPasswordCdsProps>(
  (
    {
      className,
      placeholder,
      label,
      form,
      name,
      description,
      errorMessage,
      linkLabel = 'Forgot your password?',
      linkUrl,
      ...props
    },
    ref
  ) => {
    if (!form) {
      throw new Error(`FormFieldPasswordCds 'form' is required (name: ${name}, label: ${label})`);
    }

    if (!name) {
      throw new Error(`FormFieldPasswordCds 'name' is required (label: ${label})`);
    }

    return (
      <FormField
        control={form.control}
        name={name}
        render={({ field }) => (
          <FormItem className={className} ref={ref}>
            <div className="flex justify-between items-center w-full">
              {label && <FormLabel>{label}</FormLabel>}
              {linkUrl && (
                <a className="text-sm font-normal text-black underline" href={linkUrl}>
                  {linkLabel}
                </a>
              )}
            </div>
            <FormControl>
              <InputPasswordCds {...props} {...field} placeholder={placeholder || ''} />
            </FormControl>
            {description && <FormDescription>{description}</FormDescription>}
            <FormMessage>{errorMessage || ''}</FormMessage>
          </FormItem>
        )}
      />
    );
  }
);

FormFieldPasswordCds.displayName = 'FormFieldPasswordCds';

export { FormFieldPasswordCds };
